import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  ArrowDownRight,
  ArrowUpRight,
  CheckCircle2,
  ChevronRight,
  Circle,
  Loader2,
  RefreshCw,
  Sparkles,
} from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { useCurrency } from '@/contexts/CurrencyContext'
import { useEdition } from '@/hooks/useEdition'
import { useToast } from '@/hooks/use-toast'
import { formatMoneyFixed } from '@/lib/currency'
import { cn } from '@/lib/utils'
import type { HomeFigure, MarketingHome as MarketingHomeData } from '@/types/marketingProactive'
import { MarketingShell } from '../MarketingShell'
import { dateRange, daysAway, MOMENT_TONE, parseDay } from './proactiveMeta'
import { SuggestionCard } from './SuggestionCard'
import { useBuildKit } from './useBuildKit'

/**
 * Marketing Overview (F-MKT-N1). Built like the Reports overview: one
 * headline, then what to do next. The detailed numbers moved to
 * Performance; everything here either answers "is marketing working?" or
 * hands the owner a ready-made next step.
 */
export function MarketingHome() {
  const { posEnabled } = useEdition()
  const query = useQuery({
    queryKey: ['marketing', 'home'],
    queryFn: () => apiClient.getMarketingHome(),
    staleTime: 60_000,
  })
  const data = query.data?.data
  const [allOpen, setAllOpen] = useState(false)

  return (
    <MarketingShell
      title={posEnabled ? 'Marketing Suite' : 'Overview'}
      description="What to do next, and what it's bringing in"
    >
      <div className="space-y-6">
        {query.isError && (
          <Card className="border-destructive/50">
            <CardContent className="p-4 text-sm text-destructive">Could not load the overview. Refresh to try again.</CardContent>
          </Card>
        )}

        {!data ? <Skeleton className="h-36 w-full rounded-lg" /> : <Headline data={data} posEnabled={posEnabled} />}

        {data && <SetupSteps setup={data.setup} />}

        <section className="space-y-3">
          <div className="flex items-end justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Next moves</h2>
              <p className="text-sm text-muted-foreground">
                Ideas from your calendar, stock, sales, the weather and your reviews — refreshed every few hours.
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <RefreshButton />
              {data && data.open_count > data.next_moves.length && (
                <Button size="sm" variant="ghost" onClick={() => setAllOpen(true)}>
                  See all {data.open_count}
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          {!data ? (
            <div className="space-y-2">
              <Skeleton className="h-24 w-full rounded-lg" />
              <Skeleton className="h-24 w-full rounded-lg" />
            </div>
          ) : data.next_moves.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-center">
              <p className="font-medium">Nothing needs you right now</p>
              <p className="mt-1 text-sm text-muted-foreground">
                The next idea appears here as soon as a moment, your stock, the weather or a review calls for one.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {data.next_moves.map((s) => (
                <SuggestionCard key={s.id} suggestion={s} />
              ))}
            </div>
          )}
        </section>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ComingUp data={data} />
          <RunningNow data={data} />
        </div>
      </div>

      <AllSuggestionsSheet open={allOpen} onOpenChange={setAllOpen} />
    </MarketingShell>
  )
}

function Delta({ fig, money }: { fig: HomeFigure; money?: (n: number) => string }) {
  const { current, previous } = fig
  if (previous <= 0 && current <= 0) return <span className="text-muted-foreground">No activity yet</span>
  const fmt = money ?? ((n: number) => n.toLocaleString('en-US'))
  if (previous <= 0) return <span className="text-muted-foreground">Nothing at this point last month</span>
  const up = current >= previous
  const pct = Math.round((Math.abs(current - previous) / previous) * 100)
  return (
    <span className="inline-flex items-center gap-1 text-muted-foreground">
      <span className={cn('inline-flex items-center font-medium', up ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground')}>
        {up ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
        {pct}%
      </span>
      vs {fmt(previous)} by now last month
    </span>
  )
}

function Headline({ data, posEnabled }: { data: MarketingHomeData; posEnabled: boolean }) {
  const { currencyCode } = useCurrency()
  const money = (n: number) => formatMoneyFixed(n, currencyCode, { digits: 0 })
  const h = data.headline
  const count = (n: number) => n.toLocaleString('en-US')

  const tiles = [
    { label: 'Guests who came back', fig: h.guests_back, to: '/admin/customers' },
    { label: 'New guests', fig: h.new_guests, to: '/admin/marketing/members' },
    { label: 'Messages sent', fig: h.messages, to: '/admin/marketing/campaigns' },
  ]

  return (
    <Card>
      <CardContent className="space-y-5 p-5">
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">{h.month_label} so far</p>
          {posEnabled ? (
            <p className="text-2xl font-semibold tracking-tight sm:text-3xl">
              Offers brought in {money(h.offer_sales.current)}
              <span className="text-muted-foreground font-normal">
                {' '}
                from {count(h.offer_guests.current)} guest{h.offer_guests.current === 1 ? '' : 's'} using a code or reward
              </span>
            </p>
          ) : (
            <p className="text-2xl font-semibold tracking-tight sm:text-3xl">
              {count(h.guests_back.current)} guests came back
              <span className="text-muted-foreground font-normal"> · {count(h.offer_guests.current)} used an offer or reward</span>
            </p>
          )}
          <p className="text-sm">
            <Delta fig={posEnabled ? h.offer_sales : h.guests_back} money={posEnabled ? money : undefined} />
          </p>
        </div>
        <div className="grid grid-cols-1 gap-3 border-t pt-4 sm:grid-cols-3">
          {tiles.map((t) => (
            <Link key={t.label} to={t.to} className="group rounded-md p-1 -m-1 transition-colors hover:bg-muted/40">
              <p className="text-sm text-muted-foreground">{t.label}</p>
              <p className="text-xl font-semibold">{count(t.fig.current)}</p>
              <p className="text-xs">
                <Delta fig={t.fig} />
              </p>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function SetupSteps({ setup }: { setup: MarketingHomeData['setup'] }) {
  const steps = [
    { done: setup.loyalty_enabled, label: 'Turn on your loyalty program', to: '/admin/marketing/loyalty' },
    { done: setup.active_promos > 0, label: 'Create your first promo code', to: '/admin/marketing/promo-codes' },
    { done: setup.qr_codes > 0, label: 'Make a trackable QR code', to: '/admin/marketing/qr-codes' },
  ]
  if (steps.every((s) => s.done)) return null
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-primary/30 bg-primary/[0.03] p-4 sm:flex-row sm:items-center sm:gap-4">
      <p className="text-sm font-medium">Finish setting up</p>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5">
        {steps.map((s) => (
          <Link key={s.to} to={s.to} className="inline-flex items-center gap-1.5 text-sm hover:underline">
            {s.done ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            ) : (
              <Circle className="h-4 w-4 text-muted-foreground/50" />
            )}
            <span className={cn(s.done && 'text-muted-foreground line-through')}>{s.label}</span>
          </Link>
        ))}
      </div>
    </div>
  )
}

function RefreshButton() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const refresh = useMutation({
    mutationFn: () => apiClient.refreshMarketingSuggestions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing', 'home'] })
      queryClient.invalidateQueries({ queryKey: ['marketing', 'suggestions'] })
    },
    onError: () => toast({ title: 'Could not check for new ideas', variant: 'destructive' }),
  })
  return (
    <Button size="sm" variant="ghost" disabled={refresh.isPending} onClick={() => refresh.mutate()} aria-label="Check for new ideas">
      {refresh.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
    </Button>
  )
}

function ComingUp({ data }: { data?: MarketingHomeData }) {
  const build = useBuildKit()
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-base">Coming up</CardTitle>
        <Link to="/admin/marketing/calendar" className="text-sm font-medium text-primary hover:underline">
          Calendar →
        </Link>
      </CardHeader>
      <CardContent className="space-y-1">
        {!data ? (
          <Skeleton className="h-40 w-full" />
        ) : data.upcoming.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing on the calendar for the next ten weeks.</p>
        ) : (
          data.upcoming.map((o) => {
            const d = parseDay(o.starts_on)
            const pending = build.isPending && build.variables?.occurrence_id === o.id
            return (
              <div key={o.id} className="flex items-center gap-3 rounded-md p-2 hover:bg-muted/40">
                <div className={cn('flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-md', MOMENT_TONE[o.kind] ?? MOMENT_TONE.custom)}>
                  <span className="text-[10px] font-medium uppercase leading-none">
                    {d.toLocaleDateString('en-GB', { month: 'short' })}
                  </span>
                  <span className="text-base font-semibold leading-tight">{d.getDate()}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{o.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {o.starts_on !== o.ends_on ? dateRange(o.starts_on, o.ends_on) + ' · ' : ''}
                    {daysAway(o.days_until)}
                    {o.approximate ? ' · date approximate' : ''}
                  </p>
                </div>
                {o.kit_id ? (
                  <Button size="sm" variant="outline" asChild>
                    <Link to="/admin/marketing/kits" search={{ kit: o.kit_id }}>
                      {o.kit_status === 'launched' ? 'Kit live' : 'Open kit'}
                    </Link>
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" disabled={pending} onClick={() => build.mutate({ occurrence_id: o.id })}>
                    {pending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                    Plan
                  </Button>
                )}
              </div>
            )
          })
        )}
      </CardContent>
    </Card>
  )
}

function RunningNow({ data }: { data?: MarketingHomeData }) {
  const { currencyCode } = useCurrency()
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-base">Running now</CardTitle>
        <Link to="/admin/marketing/kits" className="text-sm font-medium text-primary hover:underline">
          All kits →
        </Link>
      </CardHeader>
      <CardContent className="space-y-1">
        {!data ? (
          <Skeleton className="h-40 w-full" />
        ) : data.running.length === 0 ? (
          <div className="flex items-start gap-3 rounded-md bg-muted/30 p-3">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Launch a kit from Next moves or the calendar and it shows up here with live results.
            </p>
          </div>
        ) : (
          data.running.map((k) => (
            <Link
              key={k.id}
              to="/admin/marketing/kits"
              search={{ kit: k.id }}
              className="flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-muted/40"
            >
              {k.products[0]?.image_url ? (
                <img src={k.products[0].image_url} alt="" className="h-11 w-11 shrink-0 rounded-md object-cover" />
              ) : (
                <div className="h-11 w-11 shrink-0 rounded-md bg-muted" />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{k.title}</p>
                <p className="text-xs text-muted-foreground">
                  {dateRange(k.starts_on, k.ends_on)}
                  {k.code ? ` · ${k.code}` : ''}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold">{k.redemptions} used</p>
                <p className="text-xs text-muted-foreground">{formatMoneyFixed(k.revenue, currencyCode, { digits: 0 })}</p>
              </div>
            </Link>
          ))
        )}
      </CardContent>
    </Card>
  )
}

function AllSuggestionsSheet({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const query = useQuery({
    queryKey: ['marketing', 'suggestions', 'open'],
    queryFn: () => apiClient.listMarketingSuggestions('open'),
    enabled: open,
  })
  const list = query.data?.data?.suggestions ?? []
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>All ideas</SheetTitle>
          <SheetDescription>Everything open or snoozed, most useful first.</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-2">
          {query.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : list.length === 0 ? (
            <p className="text-sm text-muted-foreground">No open ideas.</p>
          ) : (
            list.map((s) => <SuggestionCard key={s.id} suggestion={s} />)
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
